import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { IdentityNavigationService } from '../../../identity/services/identity-navigation.service';
import { ParentIdentityService } from '../../../identity/services/parent-identity.service';
import { FamilyHubService } from '../../../parent-family/services/family-hub.service';
import { ParentFamilyContextService } from '../../../parent-family/parent-family-context.service';
import {
  FamilyKidsService,
  type FamilyChild,
} from '../../../parent-family/services/family-kids.service';
import { UserService } from '../../../user/service/user.service';
import { HomeDialogService } from '../../services/home-dialog.service';
import { GaEventService } from '../../../../core/services/ga.service';
import {
  PARENT_ANALYTICS_LAYOUT,
  ParentAnalyticsEvents,
  ParentAnalyticsTabs,
} from '../../../../core/analytics/parent-analytics.events';
import {
  hasKidsMaxSeatAvailable,
  showKidsMaxUpgrade,
} from '../../../user/utils/plan-kids-limit.util';

@Component({
  selector: 'app-home-welcome',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslatePipe],
  templateUrl: './home-welcome.component.html',
  styleUrl: './home-welcome.component.scss',
})
export class HomeWelcomeComponent {
  readonly #userService = inject(UserService);
  readonly #family = inject(ParentFamilyContextService);
  readonly #familyKids = inject(FamilyKidsService);
  readonly #familyHub = inject(FamilyHubService);
  readonly #identity = inject(ParentIdentityService);
  readonly #identityNav = inject(IdentityNavigationService);
  readonly #homeDialogs = inject(HomeDialogService);
  readonly #analytics = inject(GaEventService);
  readonly #router = inject(Router);

  protected readonly tabs = ParentAnalyticsTabs;

  readonly isLoading = computed(
    () =>
      this.#userService.isLoading() ||
      this.#familyKids.isLoading() ||
      this.#familyHub.familyQuery.isLoading(),
  );

  readonly displayName = computed(() => {
    const user = this.#userService.user();
    const first = user?.personalData?.firstName?.trim() ?? '';
    if (first) {
      return first;
    }
    return user?.userName?.trim() || '';
  });

  readonly children = this.#familyKids.children;
  readonly hasChildren = this.#familyKids.hasChildren;

  readonly needsIdentity = computed(
    () =>
      this.#identity.layerEnabled() &&
      this.#identity.loaded() &&
      this.#identity.shouldPromptVerification(),
  );

  readonly canAddChildByRole = computed(() => {
    const uid = this.#userService.user()?.id ?? '';
    const role = this.#familyHub.familyQuery
      .data()
      ?.members?.find((m) => m.userId === uid)?.role;
    return role === 'PRIMARY' || role === 'CO_PARENT';
  });

  readonly kidsMaxPlanContext = computed(() => ({
    quotasEnforced: this.#userService.quotasEnforced(),
    paymentsEnabled: this.#userService.paymentsEnabled(),
    scopes: this.#userService.entitlement()?.scopes,
    activeChildCount: this.#familyKids.activeChildCount(),
  }));

  readonly canAddChildByPlan = computed(() =>
    hasKidsMaxSeatAvailable(this.kidsMaxPlanContext()),
  );

  readonly showKidsUpgrade = computed(() =>
    showKidsMaxUpgrade({
      ...this.kidsMaxPlanContext(),
      canAddByRole: this.canAddChildByRole(),
    }),
  );

  readonly canAddChild = computed(
    () =>
      this.canAddChildByRole() &&
      this.canAddChildByPlan() &&
      (!this.#identity.layerEnabled() ||
        !this.#identity.loaded() ||
        this.#identity.canExpandFamily()),
  );

  readonly pendingInvite = computed(() => {
    const incoming = this.#familyHub.invitesQuery.data()?.incoming ?? [];
    return incoming.find((inv) => inv.status === 'pending') ?? null;
  });

  readonly selectedChildLabel = computed(() => {
    const selectedId = this.#family.selectedChildId()?.trim();
    if (!selectedId) {
      return '';
    }
    const child = this.#familyKids.childById(selectedId);
    return this.childDisplayName(child);
  });

  readonly pendingDutiesTotal = computed(() =>
    this.children().reduce(
      (sum, child) => sum + (child.pendingDutyCount ?? 0),
      0,
    ),
  );

  readonly incompleteTasksTotal = computed(() =>
    this.children().reduce(
      (sum, child) => sum + (child.incompleteTaskCount ?? 0),
      0,
    ),
  );

  readonly attentionTotal = computed(
    () => this.pendingDutiesTotal() + this.incompleteTasksTotal(),
  );

  openAddKid(): void {
    if (this.showKidsUpgrade()) {
      void this.#router.navigate(['/profile'], {
        queryParams: { segment: 'subscriptions' },
      });
      return;
    }
    this.#homeDialogs.openAddKidDialog();
  }

  goVerify(): void {
    void this.#identityNav.navigateToVerification();
  }

  onDestinationClick(
    tab: typeof ParentAnalyticsTabs.DUTIES | typeof ParentAnalyticsTabs.COURSES,
  ): void {
    this.#analytics.sendEvent(ParentAnalyticsEvents.CLICK_TAB, {
      layout: PARENT_ANALYTICS_LAYOUT,
      tab_name: tab,
      source: 'home_welcome',
    });
  }

  private childDisplayName(child: FamilyChild | undefined): string {
    if (!child) {
      return '';
    }
    return this.#familyKids.displayName(child);
  }
}
